import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Menu, X, User } from 'lucide-react';
import { MenuItem, UserGroup } from '../types';
import { ICON_MAP } from '../constants';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '../hooks/useAuth';
import { useMenu } from '../hooks/useMenu';
import { useConfig } from '../hooks/useConfig';
import { usePermission } from '../hooks/usePermission';
import { AccessLevel } from '../types';
import { SignedImage } from './SignedImage';
import { LogoutModal } from './LogoutModal';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

const SidebarItem: React.FC<{ 
  item: MenuItem; 
  depth?: number;
  isExpanded?: boolean;
  onToggleExpand: (label: string) => void;
  isExpandedCheck: (label: string) => boolean;
  onClearExpand?: () => void;
  onClose?: () => void;
  onLogoutRequest?: () => void;
}> = ({ item, depth = 0, isExpanded, onToggleExpand, isExpandedCheck, onClearExpand, onClose, onLogoutRequest }) => {
  const { user } = useAuth();
  const { getTileStyles } = useConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const configs = getTileStyles();
  const highlightColor = configs.hilight_color;
  
  const tileStyles = getTileStyles(item.item);
  const IconFromConfig = tileStyles.icon ? ICON_MAP[tileStyles.icon] : null;
  const iconPathFromConfig = tileStyles.iconPath;
  
  // Depth 0: marginLeft = 0, textAt = 12 + 16 + 10 = 38
  // First letter of level N is at: Left(N) + Padding(N) + Icon(N) + Gap(N)
  const basePaddingLeft = 24; // Spazio per lo chevron
  const iconSize = 14;
  const iconGap = 6;
  
  const indentPerLevel = 10; // Indentazione più sottile
  const marginLeft = depth * indentPerLevel;

  const IconFromMenu = item.icon ? ICON_MAP[item.icon] : null;
  const Icon = IconFromConfig || IconFromMenu;
  const iconPath = iconPathFromConfig || item.iconPath;

  const hasChildren = item.children && item.children.length > 0;
  
  const isMobile = () => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 1024;
  };

  const handleNavigation = (route: string, shouldClose: boolean = true) => {
    navigate(route);
    // Assicuriamoci che in Desktop (>= 1024) la sidebar non si chiuda MAI automaticamente
    if (shouldClose && onClose && isMobile()) {
      onClose();
    }
  };

  const isActive = item.route && (location.pathname === item.route || (item.route !== '/' && location.pathname.startsWith(item.route + '/')));

  const handleClick = (e: React.MouseEvent) => {
    if (item.item === 'Uscita') {
      e.preventDefault();
      e.stopPropagation();
      if (onLogoutRequest) onLogoutRequest();
      return;
    }

    if (hasChildren) {
      // Per i nodi padre, vogliamo gestire l'espansione
      e.preventDefault();
      e.stopPropagation();

      const targetRoute = item.route || 
                        item.children?.find(c => c.item === 'Dashboard' && c.route)?.route || 
                        item.children?.find(c => c.route)?.route;
                        
      onToggleExpand(item.item);
                        
      if (targetRoute) {
        handleNavigation(targetRoute, false);
      }
    } else {
      // Per i nodi foglia, lasciamo che il NavLink gestisca la navigazione, 
      // ma intercettiamo solo se siamo su mobile per chiudere la sidebar
      if (isMobile() && onClose) {
        // Se è mobile, chiudiamo la sidebar dopo un breve delay per permettere il rendering della rotta
        setTimeout(onClose, 100);
      }
      
      if (depth === 0 && onClearExpand) onClearExpand();
    }
  };

  const renderIcon = () => {
    if (item.item === 'Io' && user?.avatar_url) {
      return (
        <div className="w-5 h-5 mr-3 shrink-0 overflow-hidden rounded-full border border-slate-200 bg-slate-50 flex items-center justify-center">
          <SignedImage 
            path={user.avatar_url} 
            alt="Profile" 
            className="w-full h-full object-cover"
            fallback={
              user.avatar_url?.startsWith('http') ? (
                <img src={user.avatar_url} alt="Profile" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
              ) : (
                <User className="w-3 h-3 text-slate-300" />
              )
            }
          />
        </div>
      );
    }

    const iconStyle = {
      backgroundColor: tileStyles.sfondo_icona || 'transparent',
      color: tileStyles.colore_icona || 'inherit',
      borderColor: tileStyles.bordo_icona_colore || 'transparent',
      borderWidth: tileStyles.bordo_icona_spessore || '0px',
      borderStyle: 'solid'
    };

    if (iconPath && !imgError) {
      return (
        <div className="w-7 h-7 flex items-center justify-center rounded-lg mr-3 shrink-0 border" style={iconStyle}>
          <SignedImage 
            path={iconPath} 
            bucket="cpgev"
            alt="" 
            className="w-4 h-4 object-contain"
            onError={() => setImgError(true)}
          />
        </div>
      );
    }
    if (Icon) {
      return (
        <div className="w-7 h-7 flex items-center justify-center rounded-lg mr-3 shrink-0 border" style={iconStyle}>
          <Icon className="w-4 h-4" />
        </div>
      );
    }
    return null;
  };

  const innerContent = (
    <>
      {hasChildren && (
        <div className="absolute left-1.5 top-1/2 -translate-y-1/2 z-10">
          {isExpanded ? (
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground/80" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/80" />
          )}
        </div>
      )}
      {renderIcon()}
      <span className="truncate flex-1 leading-tight">{item.item}</span>
    </>
  );

  const commonStyles = {
    marginLeft: `${marginLeft}px`,
    marginBottom: tileStyles.margini,
    paddingLeft: `${basePaddingLeft}px`,
    paddingTop: tileStyles.padding,
    paddingBottom: tileStyles.padding,
    paddingRight: tileStyles.padding,
    backgroundColor: isActive ? tileStyles.selected : isHovered ? tileStyles.mouseover : tileStyles.sfondo,
    color: isActive ? '#fff' : isHovered ? tileStyles.voci : tileStyles.unselected,
    fontFamily: tileStyles.tipo_carattere,
    fontSize: tileStyles.dimensione,
    fontWeight: tileStyles.formato === 'bold' ? 'bold' : 'normal',
    fontStyle: tileStyles.formato === 'italic' ? 'italic' : 'normal',
    textDecoration: tileStyles.formato === 'underline' ? 'underline' : 'none',
    minHeight: '26px'
  };

  const content = (
    <div
      className={cn(
        "group relative flex items-center transition-all cursor-pointer overflow-hidden rounded-md",
        isActive && "z-10"
      )}
      style={commonStyles}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {innerContent}
    </div>
  );

  const lineLeft = marginLeft + basePaddingLeft + (iconSize / 2);

  if (hasChildren) {
    return (
      <div className="relative">
        {content}
        
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="relative"
            >
              {/* Vertical Tree Line */}
              <div 
                className="absolute border-l border-slate-300 top-0 bottom-5 z-0"
                style={{ left: `${lineLeft}px` }}
              />
              
              {item.children?.map((child, idx) => (
                <div key={child.item + idx} className="relative">
                  {/* Horizontal Tree Line connector */}
                  <div 
                    className="absolute border-t border-slate-300 w-3 top-[17px] z-0"
                    style={{ left: `${lineLeft}px` }}
                  />
                  <SidebarItem 
                    item={child} 
                    depth={depth + 1} 
                    isExpanded={isExpandedCheck(child.item)}
                    onToggleExpand={onToggleExpand}
                    isExpandedCheck={isExpandedCheck}
                    onClearExpand={onClearExpand}
                    onClose={onClose}
                    onLogoutRequest={onLogoutRequest}
                  />
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  const getRoute = () => {
    if ((item.item === 'Io' || item.item === 'Profilo' || item.item === 'Anagrafica GEV') && user?.personaId) {
      return `/user/profilo/${user.personaId}`;
    }
    return item.route || '#';
  };

  return (
    <NavLink
      to={getRoute()}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={({ isActive: linkActive }) => cn(
        "group relative flex items-center w-full transition-all rounded cursor-pointer",
        (isActive || linkActive) && "z-10 shadow-sm"
      )}
      style={{
        ...commonStyles,
        backgroundColor: isActive ? tileStyles.selected : isHovered ? tileStyles.mouseover : tileStyles.sfondo,
        color: isActive ? '#fff' : isHovered ? tileStyles.voci : tileStyles.unselected,
      }}
    >
      {innerContent}
    </NavLink>
  );
};

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { hasPermission, hasGroup } = usePermission();
  const { menu: items, loading: menuLoading, error: menuError, refreshMenu, realtimeStatus } = useMenu();
  const { loading: configLoading, error: configError, refreshConfig, getTileStyles } = useConfig();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set(['Io']));
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const handleManualRefresh = async () => {
    await Promise.all([refreshMenu(), refreshConfig()]);
  };

  const handleLogout = async () => {
    setIsLogoutModalOpen(false);
    await signOut();
  };

  const loading = menuLoading || configLoading;
  const error = menuError || configError;
  const isApiDisabled = (menuError?.code === 'PGRST002') || (configError?.code === 'PGRST002');

  // Recursive function to filter menu items based on user groups/permissions
  const filterMenuItems = useCallback((menuItems: MenuItem[]): MenuItem[] => {
    return menuItems.filter(item => {
      // Admin bypass: if user has Admin role, show everything
      if (user?.role === 'Admin') {
        return true;
      }
      
      let hasAccess = true;
      
      // Check auth.view, permission_key, or requiredGroup
      const accessKey = item.auth?.view || item.permission_key || item.permissionKey || item.requiredGroup;
      
      if (accessKey) {
        // Se la chiave richiede esattamente "app", controlliamo l'esperienza generale (tutti i soci hanno il gruppo "app")
        if (accessKey === 'app') {
          hasAccess = hasGroup('app');
        } else {
          // Altrimenti usiamo il controllo dei permessi gerarchico con usePermission
          hasAccess = hasPermission(accessKey, AccessLevel.ro);
        }
      }

      // Special case: if no specific access key is defined, we might default to showing it 
      // or checking children. The user wants parents to be visible if they have access to the group.
      
      if (item.children && item.children.length > 0) {
        const filteredChildren = filterMenuItems(item.children);
        // Visible if:
        // 1. User has direct access to the parent (if accessKey exists)
        // 2. OR User doesn't have a direct accessKey but has access to at least one child
        if (!accessKey) return filteredChildren.length > 0;
        return hasAccess || filteredChildren.length > 0;
      }
      
      return hasAccess;
    }).map(item => {
      if (item.children && item.children.length > 0) {
        return { ...item, children: filterMenuItems(item.children) };
      }
      return item;
    });
  }, [user, hasPermission, hasGroup]);

  const filteredItems = useMemo(() => filterMenuItems(items), [items, filterMenuItems]);

  // Auto-expand based on current route
  const lastProcessedPathname = useRef<string>('');

  useEffect(() => {
    const currentPath = location.pathname;
    
    // Solo esecuzione quando il percorso cambia davvero, per evitare cicli di rendering infiniti
    if (lastProcessedPathname.current === currentPath) {
      return;
    }
    lastProcessedPathname.current = currentPath;

    if (menuLoading) return;

    const newExpanded = new Set<string>();

    // Se siamo su /user o sue sotto-rotte, espandiamo per forza "Io"
    if (currentPath === '/user' || currentPath.startsWith('/user/')) {
      newExpanded.add('Io');
    }

    // Ricerca ricorsiva del percorso attivo per espandere i nodi genitore
    const findActivePath = (menuItems: MenuItem[]): boolean => {
      let matched = false;
      for (const item of menuItems) {
        let currentItemMatches = false;
        if (item.route) {
          const isExact = currentPath === item.route;
          const isSubPath = item.route !== '/' && currentPath.startsWith(item.route + '/');
          if (isExact || isSubPath) {
            currentItemMatches = true;
            if (item.children && item.children.length > 0) {
              newExpanded.add(item.item);
            }
          }
        }
        if (item.children) {
          if (findActivePath(item.children)) {
            newExpanded.add(item.item);
            currentItemMatches = true;
          }
        }
        if (currentItemMatches) matched = true;
      }
      return matched;
    };

    findActivePath(filteredItems);

    setExpandedItems(prev => {
      // Se il set calcolato coincide esattamente con quello precedente, evitiamo aggiornamenti di stato superflui
      if (prev.size === newExpanded.size && [...prev].every(val => newExpanded.has(val))) {
        return prev;
      }
      return newExpanded;
    });
  }, [location.pathname, filteredItems, menuLoading]);

  const handleToggleExpand = (itemLabel: string) => {
    setExpandedItems(prev => {
      if (prev.has(itemLabel)) {
        const next = new Set(prev);
        next.delete(itemLabel);
        return next;
      }
      
      // When opening a new item, we want to close all other branches at that level
      // and only keep the path to this specific item expanded.
      const path: string[] = [];
      const findPath = (menuItems: MenuItem[], target: string, currentPath: string[]): boolean => {
        for (const item of menuItems) {
          if (item.item === target) {
            path.push(...currentPath, item.item);
            return true;
          }
          if (item.children) {
            if (findPath(item.children, target, [...currentPath, item.item])) {
              return true;
            }
          }
        }
        return false;
      };

      findPath(filteredItems, itemLabel, []);
      
      // If we found a path, use it. Otherwise fallback to toggle behavior.
      if (path.length > 0) {
        return new Set(path);
      }

      const next = new Set(prev);
      next.add(itemLabel);
      return next;
    });
  };

  return (
    <>
      {/* Mobile Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={onToggle}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{ 
          width: isOpen ? 290 : (typeof window !== 'undefined' && window.innerWidth >= 1024 ? 0 : 290),
          x: isOpen ? 0 : (typeof window !== 'undefined' && window.innerWidth >= 1024 ? 0 : -290),
          opacity: isOpen ? 1 : (typeof window !== 'undefined' && window.innerWidth >= 1024 ? 1 : 0)
        }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex flex-col bg-card border-r lg:static overflow-hidden",
          !isOpen && "border-none lg:w-0"
        )}
      >
        <div className="flex items-center justify-between h-16 px-6 border-b shrink-0">
          <div className="flex items-center space-x-3 overflow-hidden">
            <div className="w-8 h-8 flex items-center justify-center shrink-0 overflow-hidden rounded-lg">
              <img 
                src="/images/gufetto.webp" 
                alt="GEV Bologna Logo" 
                className="w-full h-full object-contain"
                referrerPolicy="no-referrer"
              />
            </div>
            <span className="text-xl font-bold truncate">Gesvol Menu</span>
          </div>
          <button 
            onClick={onToggle} 
            className="lg:hidden p-2 rounded-lg hover:bg-accent transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <nav 
          className="flex-1 overflow-y-auto" 
          style={{ 
            backgroundColor: getTileStyles("Menù").sfondo,
            borderRightWidth: getTileStyles("Menù").bordo_spessore,
            borderRightStyle: getTileStyles("Menù").bordo_tipo as any,
            borderRightColor: getTileStyles("Menù").bordo_colore
          }}
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center py-10 space-y-4">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-xs text-muted-foreground animate-pulse">Caricamento menù...</p>
            </div>
          ) : error ? (
            <div className="px-4 py-6 text-center space-y-3">
              <p className="text-xs text-destructive font-medium">Errore caricamento menù</p>
              <button 
                onClick={() => refreshMenu()}
                className="text-xs bg-primary/10 text-primary px-3 py-1.5 rounded-lg hover:bg-primary/20 transition-colors"
              >
                Riprova
              </button>
            </div>
          ) : (
            filteredItems.map((item, idx) => (
              <SidebarItem 
                key={`${item.item}-${idx}-${item.route || ''}`} 
                item={item} 
                isExpanded={expandedItems.has(item.item)}
                onToggleExpand={handleToggleExpand}
                isExpandedCheck={(label) => expandedItems.has(label)}
                onClearExpand={() => setExpandedItems(new Set())}
                onClose={onToggle}
                onLogoutRequest={() => setIsLogoutModalOpen(true)}
              />
            ))
          )}
        </nav>

        <LogoutModal 
          isOpen={isLogoutModalOpen} 
          onClose={() => setIsLogoutModalOpen(false)} 
          onConfirm={handleLogout} 
        />


        {/* Realtime Status Indicator */}
        <div className="px-6 py-4 border-t bg-accent/5">
          <div className="flex items-center justify-between text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">
            <span>System Status</span>
            <div className="flex items-center space-x-1.5">
              <div className={cn(
                "w-2 h-2 rounded-full",
                realtimeStatus === 'connecting' ? "bg-yellow-500 animate-pulse" : 
                realtimeStatus === 'disabled' ? "bg-slate-400" :
                error ? "bg-red-500" : "bg-green-500"
              )} />
              <span className={cn(
                realtimeStatus === 'connecting' ? "text-yellow-600" : 
                realtimeStatus === 'disabled' ? "text-slate-500" :
                error ? "text-red-600" : "text-green-600"
              )}>
                {realtimeStatus === 'connecting' ? "Connecting" : 
                 realtimeStatus === 'disabled' ? "Realtime Off" :
                 isApiDisabled ? "API DISABLED" : error ? "Error" : "Connected"}
              </span>
            </div>
          </div>
          {isApiDisabled ? (
            <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-[9px] text-red-600 font-bold leading-tight">
                CRITICAL: Supabase API cache is stale.
              </p>
              <p className="mt-1 text-[8px] text-red-500 leading-tight">
                Run: <code className="bg-red-500/20 px-1 rounded">NOTIFY pgrst, 'reload schema';</code> in SQL Editor.
              </p>
              <button 
                onClick={handleManualRefresh}
                className="mt-2 w-full text-[8px] bg-red-600 text-white py-1 rounded hover:bg-red-700 transition-colors uppercase font-bold"
              >
                Ricarica Schema & App
              </button>
            </div>
          ) : error ? (
            <div className="mt-2 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <p className="text-[9px] text-yellow-600 font-bold leading-tight">
                Realtime Connection Issue
              </p>
              <p className="mt-1 text-[8px] text-yellow-500 leading-tight">
                Check Docker logs for <code className="bg-yellow-500/20 px-1 rounded">realtime</code> container.
              </p>
              <button 
                onClick={handleManualRefresh}
                className="mt-2 w-full text-[8px] bg-yellow-600 text-white py-1 rounded hover:bg-yellow-700 transition-colors uppercase font-bold"
              >
                Riconnetti
              </button>
            </div>
          ) : null}
        </div>
      </motion.aside>
    </>
  );
};
